import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import BillingModalInvoiceReceiptComponent from '@/modules/billing-module/components/modal/invoice-receipt/billing-modal-invoice-receipt.vue'
import BillingModalInvoiceReceipt from '@/modules/billing-module/components/modal/invoice-receipt/billing-modal-invoice-receipt'
import { InvoiceReceiptStatus, InvoiceReceiptResponse } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'
import { billingInvoiceReceiptService } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.service'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { FileUtils } from '@/shared/util/file-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component({
    components: {
        BillingModalInvoiceReceiptComponent
    }
})
export default class BillingGridActionsInvoiceReceipts extends Vue {
    @Ref() readonly invoiceReceiptModal!: BillingModalInvoiceReceipt

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): InvoiceReceiptResponse[] {
        return BillingStoreHelper.masterSelectedRows as InvoiceReceiptResponse[]
    }
    private static get _selectedRow(): StrictNullable<InvoiceReceiptResponse> {
        return BillingGridActionsInvoiceReceipts._selectedRows.length === 1
            ? BillingGridActionsInvoiceReceipts._selectedRows[0]
            : null
    }

    get canUpdateInvoiceReceipt(): boolean {
        return BillingGridActionsInvoiceReceipts._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_RECEIPTS)
    }
    get canDeleteInvoiceReceipt(): boolean {
        return BillingGridActionsInvoiceReceipts._selectedRow?.status.code === InvoiceReceiptStatus.GENERATED &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_RECEIPTS)
    }
    get canOpenInvoiceReceiptReport(): boolean {
        return BillingGridActionsInvoiceReceipts._selectedRows.length === 1
    }

    /// SERVICE CALLS ///

    private _downloadInvoiceReceiptReport(invoiceReceiptId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingInvoiceReceiptService.getReport(store.getters.labId, invoiceReceiptId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    onUpdateInvoiceReceipt(): void {
        if (BillingGridActionsInvoiceReceipts._selectedRow) {
            this.invoiceReceiptModal.show({
                invoice: BillingGridActionsInvoiceReceipts._selectedRow.invoice,
                invoiceReceipt: BillingGridActionsInvoiceReceipts._selectedRow
            })
        }
    }

    async onDeleteInvoiceReceipt(): Promise<void> {
        if (BillingGridActionsInvoiceReceipts._selectedRow) {
            const { id, appliedBillableConceptCount } = BillingGridActionsInvoiceReceipts._selectedRow
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    const response =
                        await PromiseUtils.toCallSecure(
                            billingInvoiceReceiptService.delete(store.state.labId, id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('accounting.invoiceReceipt.cancelled'))
                        this.refresh()
                    }
                }
            )
        }
    }

    async onOpenInvoiceReceiptReport(): Promise<void> {
        if (BillingGridActionsInvoiceReceipts._selectedRow) {
            const blob = await this._downloadInvoiceReceiptReport(BillingGridActionsInvoiceReceipts._selectedRow.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canUpdateInvoiceReceipt) {
            menuItems.push({
                name: this.$tc('accounting.action.updateInvoiceReceipt'),
                action: this.onUpdateInvoiceReceipt
            })
        }
        if (this.canDeleteInvoiceReceipt) {
            menuItems.push({
                name: this.$tc('accounting.action.deleteInvoiceReceipt'),
                action: this.onDeleteInvoiceReceipt
            })
        }
        if (this.canOpenInvoiceReceiptReport) {
            menuItems.push({
                name: this.$tc('global.action.openReport'),
                action: this.onOpenInvoiceReceiptReport
            })
        }
        return menuItems
    }
}
